import React, { useState, useEffect, useCallback } from 'react';
import { api, NarudzbenicaFormData, Stavka } from '../api';
import 'bootstrap/dist/css/bootstrap.min.css';

function formatHr(num: number): string {
  return num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function parseHr(str: string): number {
  if (!str) return 0;
  return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
}

interface Props {
  editId: number | null;
  data: Awaited<ReturnType<typeof api.narudzbenice.list>>;
  filterGodina: number;
  onClose: () => void;
  onSaved: () => void;
}

export default function NarudzbenicaModal({ editId, data, filterGodina, onClose, onSaved }: Props) {
  const [csrfToken, setCsrfToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stavke, setStavke] = useState<Partial<Stavka>[]>([]);
  const [form, setForm] = useState({
    broj_narudzbenice: '',
    tip_nabave: 'roba',
    broj_ponude: '',
    datum_ponude: '',
    nas_znak: '',
    nas_broj: '',
    datum: new Date().toISOString().slice(0, 10),
    kupac_naziv: '',
    kupac_adresa: '',
    kupac_oib: '',
    kupac_iban: '',
    isporucitelj_naziv: '',
    isporucitelj_adresa: '',
    isporucitelj_oib: '',
    nacin_otpreme: '',
    nacin_otpreme_tip: '',
    rok_isporuke: '',
    nacin_placanja: '',
    platiti_u_roku: '',
    sastavio_ime: '',
    kupac_select: '',
    isporucitelj_select: '',
  });

  const { kupci, isporucitelji, defaults } = data;

  useEffect(() => {
    api.auth.csrf().then((r) => setCsrfToken(r.csrf_token)).catch(() => {});
  }, []);

  const loadForEdit = useCallback(async () => {
    if (!editId) return;
    const res = await api.narudzbenice.get(editId);
    const n = res.narudzbenica;
    setForm({
      broj_narudzbenice: n.broj_narudzbenice,
      tip_nabave: n.tip_nabave,
      broj_ponude: String(n.broj_ponude || ''),
      datum_ponude: String(n.datum_ponude || ''),
      nas_znak: String(n.nas_znak || ''),
      nas_broj: String(n.nas_broj || ''),
      datum: n.datum,
      kupac_naziv: String(n.kupac_naziv || ''),
      kupac_adresa: String(n.kupac_adresa || ''),
      kupac_oib: String(n.kupac_oib || ''),
      kupac_iban: String(n.kupac_iban || ''),
      isporucitelj_naziv: String(n.isporucitelj_naziv || ''),
      isporucitelj_adresa: String(n.isporucitelj_adresa || ''),
      isporucitelj_oib: String(n.isporucitelj_oib || ''),
      nacin_otpreme: String(n.nacin_otpreme || ''),
      nacin_otpreme_tip: String(n.nacin_otpreme_tip || ''),
      rok_isporuke: String(n.rok_isporuke || ''),
      nacin_placanja: String(n.nacin_placanja || ''),
      platiti_u_roku: String(n.platiti_u_roku || ''),
      sastavio_ime: String(n.sastavio_ime || ''),
      kupac_select: '',
      isporucitelj_select: '',
    });
    setStavke(res.stavke.map((s) => ({
      naziv_dobra_usluge: s.naziv_dobra_usluge,
      jed_mjere: s.jed_mjere,
      kolicina: s.kolicina,
      cijena_bez_pdv: s.cijena_bez_pdv,
      rabat_postotak: s.rabat_postotak || 0,
      pdv_stopa: s.pdv_stopa,
      iznos: s.iznos,
    })));
  }, [editId]);

  const loadForNew = useCallback(() => {
    setForm((f) => ({
      ...f,
      datum: new Date().toISOString().slice(0, 10),
      broj_narudzbenice: '',
    }));
    setStavke([{ naziv_dobra_usluge: '', jed_mjere: 'kom', kolicina: 0, cijena_bez_pdv: 0, rabat_postotak: 0, pdv_stopa: 25, iznos: 0 }]);
    if (defaults?.default_kupac_id) {
      api.firme.get(Number(defaults.default_kupac_id)).then((r) => {
        if (r.success) {
          setForm((prev) => ({
            ...prev,
            kupac_naziv: r.naziv,
            kupac_adresa: r.adresa,
            kupac_oib: r.oib,
            kupac_iban: r.iban || '',
            kupac_select: defaults.default_kupac_id || '',
          }));
        }
      });
    }
    if (defaults?.default_isporucitelj_id) {
      api.firme.get(Number(defaults.default_isporucitelj_id)).then((r) => {
        if (r.success) {
          setForm((prev) => ({
            ...prev,
            isporucitelj_naziv: r.naziv,
            isporucitelj_adresa: r.adresa,
            isporucitelj_oib: r.oib,
            isporucitelj_select: defaults.default_isporucitelj_id || '',
          }));
        }
      });
    }
  }, [defaults]);

  useEffect(() => {
    if (editId) loadForEdit();
    else loadForNew();
  }, [editId, loadForEdit, loadForNew]);

  useEffect(() => {
    if (!editId && form.datum) {
      const godina = new Date(form.datum).getFullYear();
      api.narudzbenice.getNextBroj(godina).then((r) => {
        setForm((prev) => ({ ...prev, broj_narudzbenice: r.broj }));
      }).catch(() => {});
    }
  }, [editId, form.datum]);

  const addStavka = () => {
    setStavke((s) => [...s, { naziv_dobra_usluge: '', jed_mjere: 'kom', kolicina: 0, cijena_bez_pdv: 0, rabat_postotak: 0, pdv_stopa: 25, iznos: 0 }]);
  };

  const removeStavka = (i: number) => {
    setStavke((s) => s.filter((_, idx) => idx !== i));
  };

  const updateStavka = (i: number, field: keyof Stavka, value: string | number) => {
    setStavke((s) => {
      const next = [...s];
      (next[i] as Record<string, unknown>)[field] = value;
      return next;
    });
  };

  const { ukupno_bez_pdv, ukupni_rabat, pdv_ukupno } = stavke.reduce(
    (acc, s) => {
      const k = parseHr(String(s.kolicina));
      const c = parseHr(String(s.cijena_bez_pdv));
      const r = parseHr(String(s.rabat_postotak || 0));
      const osn = k * c;
      const rabat = osn * (r / 100);
      const iznos = k * c * (1 - r / 100);
      const pdvStopa = Number(s.pdv_stopa) || 25;
      const pdv = iznos * (pdvStopa / 100);
      acc.ukupno_bez_pdv += iznos;
      acc.ukupni_rabat += rabat;
      acc.pdv_ukupno += pdv;
      return acc;
    },
    { ukupno_bez_pdv: 0, ukupni_rabat: 0, pdv_ukupno: 0 }
  );
  const ukupno_s_pdv_val = ukupno_bez_pdv + pdv_ukupno;

  const handleFirmaSelect = (tip: 'kupac' | 'isporucitelj', id: string) => {
    if (!id) return;
    api.firme.get(Number(id)).then((r) => {
      if (r.success) {
        setForm((prev) => ({
          ...prev,
          [tip + '_naziv']: r.naziv,
          [tip + '_adresa']: r.adresa,
          [tip + '_oib']: r.oib,
          ...(tip === 'kupac' && { kupac_iban: r.iban || '' }),
          [tip + '_select']: id,
        }));
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const payload: NarudzbenicaFormData = {
      action: 'save',
      csrf_token: csrfToken,
      broj_narudzbenice: form.broj_narudzbenice,
      tip_nabave: form.tip_nabave,
      broj_ponude: form.broj_ponude || undefined,
      datum_ponude: form.datum_ponude || undefined,
      nas_znak: form.nas_znak || undefined,
      nas_broj: form.nas_broj || undefined,
      datum: form.datum,
      kupac_naziv: form.kupac_naziv,
      kupac_adresa: form.kupac_adresa,
      kupac_oib: form.kupac_oib,
      kupac_iban: form.kupac_iban || undefined,
      isporucitelj_naziv: form.isporucitelj_naziv,
      isporucitelj_adresa: form.isporucitelj_adresa,
      isporucitelj_oib: form.isporucitelj_oib,
      nacin_otpreme: form.nacin_otpreme || undefined,
      nacin_otpreme_tip: form.nacin_otpreme_tip || undefined,
      rok_isporuke: form.rok_isporuke || undefined,
      nacin_placanja: form.nacin_placanja || undefined,
      platiti_u_roku: form.platiti_u_roku || undefined,
      sastavio_ime: form.sastavio_ime || undefined,
      stavke_naziv: stavke.filter((s) => s.naziv_dobra_usluge).map((s) => s.naziv_dobra_usluge!),
      stavke_jm: stavke.filter((s) => s.naziv_dobra_usluge).map((s) => s.jed_mjere || 'kom'),
      stavke_kolicina: stavke.filter((s) => s.naziv_dobra_usluge).map((s) => String(s.kolicina)),
      stavke_cijena: stavke.filter((s) => s.naziv_dobra_usluge).map((s) => String(s.cijena_bez_pdv)),
      stavke_rabat: stavke.filter((s) => s.naziv_dobra_usluge).map((s) => String(s.rabat_postotak || 0)),
      stavke_pdv: stavke.filter((s) => s.naziv_dobra_usluge).map((s) => String(s.pdv_stopa || 25)),
    };
    if (editId) payload.edit_id = editId;
    try {
      await api.narudzbenice.save(payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Greška');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ zIndex: 1055 }}>
      <div className="modal-backdrop show" style={{ zIndex: 1050 }} onClick={onClose} />
      <div className="modal-dialog modal-xl" style={{ zIndex: 1056 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{editId ? 'Ispravi narudžbenicu' : 'Nova narudžbenica'}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="row mb-3">
                <div className="col-md-3">
                  <label className="form-label">Broj narudžbenice</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.broj_narudzbenice}
                    onChange={(e) => setForm((f) => ({ ...f, broj_narudzbenice: e.target.value }))}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Tip nabave</label>
                  <select
                    className="form-select"
                    value={form.tip_nabave}
                    onChange={(e) => setForm((f) => ({ ...f, tip_nabave: e.target.value }))}
                  >
                    <option value="roba">Roba</option>
                    <option value="radovi">Radovi</option>
                    <option value="usluge">Usluge</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Broj ponude</label>
                  <input type="text" className="form-control" value={form.broj_ponude} onChange={(e) => setForm((f) => ({ ...f, broj_ponude: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Datum ponude</label>
                  <input type="date" className="form-control" value={form.datum_ponude} onChange={(e) => setForm((f) => ({ ...f, datum_ponude: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Datum *</label>
                  <input type="date" className="form-control" value={form.datum} onChange={(e) => setForm((f) => ({ ...f, datum: e.target.value }))} required />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Naš znak</label>
                  <input type="text" className="form-control" value={form.nas_znak} onChange={(e) => setForm((f) => ({ ...f, nas_znak: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Naš broj</label>
                  <input type="text" className="form-control" value={form.nas_broj} onChange={(e) => setForm((f) => ({ ...f, nas_broj: e.target.value }))} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Sastavio/la</label>
                  <input type="text" className="form-control" value={form.sastavio_ime} onChange={(e) => setForm((f) => ({ ...f, sastavio_ime: e.target.value }))} />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <h6 className="bg-warning p-2">KUPAC</h6>
                  <select className="form-select mb-2" value={form.kupac_select} onChange={(e) => handleFirmaSelect('kupac', e.target.value)}>
                    <option value="">-- Odaberi --</option>
                    {kupci.map((k) => (
                      <option key={k.id} value={k.id}>{k.naziv}</option>
                    ))}
                  </select>
                  <input type="text" className="form-control mb-2" placeholder="Naziv" value={form.kupac_naziv} onChange={(e) => setForm((f) => ({ ...f, kupac_naziv: e.target.value }))} required />
                  <textarea className="form-control mb-2" rows={2} placeholder="Adresa" value={form.kupac_adresa} onChange={(e) => setForm((f) => ({ ...f, kupac_adresa: e.target.value }))} required />
                  <input type="text" className="form-control mb-2" placeholder="OIB" value={form.kupac_oib} onChange={(e) => setForm((f) => ({ ...f, kupac_oib: e.target.value }))} pattern="[0-9]{11}" maxLength={11} required />
                  <input type="text" className="form-control" placeholder="IBAN" value={form.kupac_iban} onChange={(e) => setForm((f) => ({ ...f, kupac_iban: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <h6 className="bg-warning p-2">ISPORUČITELJ</h6>
                  <select className="form-select mb-2" value={form.isporucitelj_select} onChange={(e) => handleFirmaSelect('isporucitelj', e.target.value)}>
                    <option value="">-- Odaberi --</option>
                    {isporucitelji.map((k) => (
                      <option key={k.id} value={k.id}>{k.naziv}</option>
                    ))}
                  </select>
                  <input type="text" className="form-control mb-2" placeholder="Naziv" value={form.isporucitelj_naziv} onChange={(e) => setForm((f) => ({ ...f, isporucitelj_naziv: e.target.value }))} required />
                  <textarea className="form-control mb-2" rows={2} placeholder="Adresa" value={form.isporucitelj_adresa} onChange={(e) => setForm((f) => ({ ...f, isporucitelj_adresa: e.target.value }))} required />
                  <input type="text" className="form-control" placeholder="OIB" value={form.isporucitelj_oib} onChange={(e) => setForm((f) => ({ ...f, isporucitelj_oib: e.target.value }))} pattern="[0-9]{11}" maxLength={11} required />
                </div>
              </div>
              <h6 className="bg-warning p-2">NARUČUJEMO</h6>
              <div className="table-responsive" style={{ maxHeight: 300, overflow: 'auto' }}>
                <table className="table table-bordered table-sm">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th>R.BR.</th>
                      <th>Naziv</th>
                      <th>JM</th>
                      <th>Količina</th>
                      <th>Cijena</th>
                      <th>Rabat %</th>
                      <th>PDV %</th>
                      <th>Iznos</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {stavke.map((s, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={s.naziv_dobra_usluge || ''}
                            onChange={(e) => updateStavka(i, 'naziv_dobra_usluge', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={s.jed_mjere || 'kom'}
                            onChange={(e) => updateStavka(i, 'jed_mjere', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm text-end"
                            value={s.kolicina || ''}
                            onChange={(e) => updateStavka(i, 'kolicina', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm text-end"
                            value={s.cijena_bez_pdv || ''}
                            onChange={(e) => updateStavka(i, 'cijena_bez_pdv', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm text-end"
                            value={s.rabat_postotak ?? ''}
                            onChange={(e) => updateStavka(i, 'rabat_postotak', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={s.pdv_stopa ?? 25}
                            onChange={(e) => updateStavka(i, 'pdv_stopa', Number(e.target.value))}
                          >
                            <option value={0}>0%</option>
                            <option value={5}>5%</option>
                            <option value={13}>13%</option>
                            <option value={25}>25%</option>
                          </select>
                        </td>
                        <td className="text-end">
                          {formatHr(
                            parseHr(String(s.kolicina)) *
                            parseHr(String(s.cijena_bez_pdv)) *
                            (1 - parseHr(String(s.rabat_postotak || 0)) / 100)
                          )} €
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => removeStavka(i)}>
                            <i className="bi bi-x" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="btn btn-sm btn-secondary mt-2" onClick={addStavka}>
                + Dodaj stavku
              </button>
              <div className="row mt-3">
                <div className="col-md-3">
                  <label className="form-label">Isporuka na naslov</label>
                  <input type="text" className="form-control" value={form.nacin_otpreme} onChange={(e) => setForm((f) => ({ ...f, nacin_otpreme: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Rok isporuke</label>
                  <input type="text" className="form-control" value={form.rok_isporuke} onChange={(e) => setForm((f) => ({ ...f, rok_isporuke: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Način otpreme</label>
                  <input type="text" className="form-control" value={form.nacin_otpreme_tip} onChange={(e) => setForm((f) => ({ ...f, nacin_otpreme_tip: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Način plaćanja</label>
                  <input type="text" className="form-control" value={form.nacin_placanja} onChange={(e) => setForm((f) => ({ ...f, nacin_placanja: e.target.value }))} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Platiti u roku</label>
                  <input type="text" className="form-control" value={form.platiti_u_roku} onChange={(e) => setForm((f) => ({ ...f, platiti_u_roku: e.target.value }))} />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-6 offset-md-6">
                  <div className="p-3 bg-light rounded">
                    <table className="table table-sm mb-0">
                      <tr>
                        <td>Ukupno bez PDV-a:</td>
                        <td className="text-end"><strong>{formatHr(ukupno_bez_pdv)} €</strong></td>
                      </tr>
                      <tr className="text-danger">
                        <td>Ukupni rabat:</td>
                        <td className="text-end">- {formatHr(ukupni_rabat)} €</td>
                      </tr>
                      <tr className="table-active">
                        <td><strong>UKUPNO s PDV-om:</strong></td>
                        <td className="text-end"><strong>{formatHr(ukupno_s_pdv_val)} €</strong></td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Zatvori</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Spremanje...' : 'Spremi'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
